import React, {
    ChangeEvent,
    ChangeEventHandler,
    ComponentProps,
    FocusEvent,
    FocusEventHandler,
    forwardRef,
    HTMLAttributes,
    Ref,
    useCallback,
    useEffect,
    useImperativeHandle,
    useRef,
    useState
} from 'react';
import styled from 'styled-components';
import { Theme } from 'theme';

import { mergeRefs } from '../../helpers';
import Body01 from '../typography/Body01';
import Caption from '../typography/Caption';
import withOverriddenHandler from './withOverriddenHandler';

export interface BaseInputProps extends Omit<HTMLAttributes<HTMLInputElement>, 'onChange'> {
    assistiveText?: string;
    autoComplete?: string;
    className?: string;
    disabled?: boolean;
    focused?: boolean;
    forceShowPlaceholder?: boolean;
    fullLabelWidth?: boolean;
    hideAssistiveText?: boolean;
    iconColor?: string;
    inputRef?: Ref<HTMLInputElement>,
    label?: string;
    leftBorderSquare?: boolean;
    leftIcon?: React.ReactNode;
    maxLength?: number;
    noBorder?: boolean;
    onBlur?(event?: React.FocusEvent<HTMLInputElement>): void;
    onKeyDown?(event?: React.KeyboardEvent<HTMLInputElement>): void;
    onChange?: ChangeEventHandler<HTMLInputElement>;
    onEnter?(): void;
    onFocus?: FocusEventHandler<HTMLInputElement>;
    onRightIconClick?(): void;
    placeholder?: string;
    preRightIcon?: React.ReactNode;
    readonly?: boolean;
    required?: boolean;
    rightBorderSquare?: boolean;
    rightIcon?: React.ReactNode;
    rootContent?: React.ReactNode;
    size?: number;
    type?: string;
    valid?: boolean;
    value: string;
    width?: string;
    withoutLabel?: boolean;
}

const BaseInput = forwardRef<HTMLInputElement, BaseInputProps>(({
    assistiveText = 'Required',
    autoComplete,
    className = '',
    disabled = false,
    focused = false,
    forceShowPlaceholder = false,
    fullLabelWidth = false,
    hideAssistiveText = false,
    inputRef,
    label = '',
    leftBorderSquare,
    leftIcon = null,
    noBorder,
    onBlur,
    onChange,
    onEnter,
    onFocus,
    onKeyDown,
    onRightIconClick = () => undefined,
    placeholder = '',
    preRightIcon = null,
    readonly = false,
    required = false,
    rightBorderSquare,
    rightIcon = null,
    rootContent = null,
    type = 'text',
    valid = true,
    value = '',
    width,
    withoutLabel = false,
    ...restProps
}, ref) => {
    const [ active, setActive ] = useState(false);

    let filled = Boolean(value);

    const focusInput = useRef<HTMLInputElement>(null);

    const mergedInputRef = mergeRefs([ focusInput, inputRef ]);

    const shouldShowPlaceholder = () => (
        active || focused || (!label && placeholder) || forceShowPlaceholder
    );

    const setInactive = (event: { target: HTMLInputElement }) => {
        setActive(false);

        if (event.target.value !== '') {
            filled = true;
        }
    };

    // @ts-ignore
    useImperativeHandle(ref, () => ({
        focus: () => {
            focusInput.current?.focus();
        }
    }));

    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
        if (onChange) {
            onChange(event);
        }
    };

    const handleFocus = useCallback<FocusEventHandler<HTMLInputElement>>((event) => {
        //This click is necessary for the correct operation of the focus of some mobile devices
        focusInput?.current?.click();
        setActive(true);

        if (onFocus) {
            onFocus(event);
        }
    }, [ onFocus ]);

    const handleBlur = (event?: FocusEvent<HTMLInputElement>) => {
        if (event) {
            setInactive(event);
        }

        if (onBlur) {
            onBlur(event);
        }
    };

    useEffect(() => {
        if (focused) {
            // eslint-disable-next-line no-unused-expressions
            focusInput?.current?.focus();
        } else {
            // eslint-disable-next-line no-unused-expressions
            focusInput?.current?.blur();
        }
    }, [ focused ]);

    const labelText = () =>
        <>
            { label }

            <Asterisk disabled={ disabled }
                required={ required }
            >
                *
            </Asterisk>
        </>;

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter' && onEnter) {
            onEnter();
        }

        if (onKeyDown) {
            onKeyDown(event);
        }
    };

    return (
        <Root className={ className }
            ref={ ref }
            width={ width }
        >
            <InputWrapper active={ active || focused }
                disabled={ disabled }
                leftBorderSquare={ leftBorderSquare }
                noBorder={ noBorder }
                rightBorderSquare={ rightBorderSquare }
                valid={ valid }
                width={ width }
                className="input-wrapper"
            >
                { label && (
                    <Label active={ active || focused || forceShowPlaceholder }
                        disabled={ disabled }
                        filled={ filled }
                        fullWidth={ fullLabelWidth }
                        onClick={ () => focusInput?.current?.focus() }
                        leftIcon={ Boolean(leftIcon) }
                    >
                        { active || filled || focused || forceShowPlaceholder
                            ? <StyledCaption active={ active || focused }
                                disabled={ disabled }
                                preRightIcon={ !!preRightIcon }
                            >
                                { labelText() }
                            </StyledCaption>
                            : <StyledBody01 active={ active || focused }
                                disabled={ disabled }
                            >
                                { labelText() }
                            </StyledBody01>
                        }
                    </Label>
                ) }

                { leftIcon && (
                    <LeftIcon active={ active || focused|| forceShowPlaceholder }
                        filled={ filled }
                        withoutLabel={ withoutLabel }
                    >
                        { leftIcon }
                    </LeftIcon>
                ) }

                <InputField
                    active={ active || focused || forceShowPlaceholder }
                    autoComplete={ autoComplete }
                    disabled={ disabled }
                    filled={ filled }
                    onBlur={ handleBlur }
                    onChange={ handleChange }
                    onFocus={ handleFocus }
                    onKeyDown={ handleKeyDown }
                    placeholder={ shouldShowPlaceholder() ? placeholder : '' }
                    readOnly={ readonly }
                    ref={ mergedInputRef }
                    type={ type }
                    value={ value }
                    withoutLabel={ withoutLabel }
                    { ...restProps }
                />

                { preRightIcon &&
                    <PreRightIcon>{ preRightIcon }</PreRightIcon>
                }

                { rightIcon && (
                    <RightIcon active={ active || focused }
                        disabled={ disabled }
                        onClick={ onRightIconClick }>
                        { rightIcon }
                    </RightIcon>
                ) }
            </InputWrapper>

            <AssistiveText valid={ valid || hideAssistiveText }>
                { assistiveText }
            </AssistiveText>

            { rootContent }
        </Root>
    );
});

export { BaseInput };

const Input = withOverriddenHandler(BaseInput);

export type InputProps = ComponentProps<typeof Input>;

export default Input;

const AssistiveText = styled(Caption)<{ valid?: boolean; disabled?: boolean }>`
    position: absolute;
    white-space: nowrap;

    ${ ({ theme, valid }) => `
        top: calc(${ theme.size.size_70 } + ${ theme.size.size_5 });
        display: ${ valid ? 'none' : 'block' };
        color: ${ theme.semanticColor.error };
        left: ${ theme.size.size_20 };
    ` };
`;

const Asterisk = styled.span<{ disabled?: boolean; required?: boolean }>`
    ${ ({ disabled, theme, required }) => `
        color: ${ disabled ? theme.semanticColor.textDisabled : theme.semanticColor.primary };
        display: ${ required ? 'inline' : 'none' };
        margin-left: ${ theme.size.size_5 };
    ` };
`;

const Label = styled.div<{
    active?: boolean;
    disabled?: boolean;
    filled: boolean;
    fullWidth?: boolean;
    leftIcon?: boolean;
}>`
    position: absolute;
    transition: all 150ms ease-in;

    ${ ({ active, filled, leftIcon, theme }) => `
        left: ${ leftIcon && !filled && !active ? theme.size.size_60 : theme.size.size_20 };
        top: ${ active || filled ? theme.size.size_10 : theme.size.size_20 };
    ` }

    width: calc(100% - ${ ({ fullWidth, theme }) => (fullWidth ? '0px' : theme.size.size_70) });
`;

export const getInputCaptionColor = ({ active, disabled, theme }: {
    active?: boolean;
    disabled?: boolean;
    theme: Theme;
}) => `
    color: ${ disabled
        ? theme.semanticColor.textDisabled
        : active
            ? theme.semanticColor.primary
            : theme.semanticColor.textSecondary
};
`;

const StyledCaption = styled(Caption)<{ active?: boolean; disabled?: boolean; preRightIcon?: boolean }>`
    ${ getInputCaptionColor };

    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    width: calc(100% - ${ ({ preRightIcon, theme }) => (preRightIcon ? theme.size.size_100 : '0px') });
`;

const StyledBody01 = styled(Body01)<{ active?: boolean; disabled?: boolean }>`
    color: ${ ({ active, disabled, theme }) => (
        disabled
            ? theme.semanticColor.textDisabled
            : active
                ? theme.semanticColor.primary
                : theme.semanticColor.textSecondary
    ) };

    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    width: 100%;
`;

const IconWrapper = styled.div<{ active?: boolean; disabled?: boolean }>`
    flex-shrink: 0;
    cursor: pointer;

    ${ ({ theme }) => `
        width: calc(${ theme.size.size_20 } + ${ theme.size.size_5 });
        height: calc(${ theme.size.size_20 } + ${ theme.size.size_5 });
    ` };

    svg {
        path {
            fill: ${ ({ active, theme }) =>
        (active ? theme.color.grey.grey_80 : theme.color.grey.grey_60) };
        }
    }

    @media (hover: hover) {
        &:hover {
            svg {
                path {
                    fill: ${ ({ disabled, theme }) => (disabled ? theme.semanticColor.textDisabled  :  theme.color.grey.grey_80 ) };
                }
            }
        }
    }
`;

const LeftIcon = styled(IconWrapper)<{
    active: boolean;
    filled?: boolean;
    withoutLabel?: boolean;
}>`
    margin-right: ${ ({ theme }) => theme.size.size_15 };

    ${ ({ active, filled, withoutLabel }) =>
        !withoutLabel &&
        (active || filled
            ? `
        transition: visibility all 150ms;
        visibility: hidden;
        width: 0;
    `
            : `
        transition: visibility 150ms 150ms;
        visibility: visible;
    `) };
`;

const PreRightIcon = styled(IconWrapper)`
    display: inline-flex;
    margin-left: auto;
    text-align: right;
    width: fit-content;
    svg {
        max-width: ${ ({ theme }) => theme.size.size_25 };
    }
`;

const RightIcon = styled(IconWrapper)< { disabled: boolean }>`
    cursor: pointer;
    margin-left: ${ ({ theme }) => theme.size.size_15 };

    ${ ({ disabled, theme }) => (disabled ? `svg path { fill: ${ theme.semanticColor.textDisabled }; }`: '') }
`;

const Root = styled.div< { width?: string }>`
    display: flex;
    position: relative;
    width: ${ ({ width }) => (width ? width : '100%') };
`;

export const getBaseInputStyles = ({ active, theme }: {
    active: boolean;
    theme: Theme;
}) => `
    background-color: transparent;
    border: none;
    ${ active ? `caret-color: ${ theme.semanticColor.primary }` : '' };
    color: ${ active ? theme.semanticColor.textPrimary : theme.color.grey.grey_80 };
    font-family: ${ theme.type.regularFont };
    font-size: 16px;
    outline: none;
    
    &:disabled {
        color: ${ theme.semanticColor.textDisabled };
    }

    ::placeholder {
        color: ${ active ? theme.color.grey.grey_70 : theme.semanticColor.textSecondary };
    }
`;

const InputField = styled.input<{
    active: boolean;
    filled: boolean;
    withoutLabel?: boolean;
}>`
    ${ getBaseInputStyles }
    
    ${ ({ active, filled, theme, withoutLabel }) => {
        const shifted = !withoutLabel && (active || filled);
        
        if (shifted) {
            return `
                line-height: ${ theme.size.size_30 };
                padding: ${ theme.size.size_30 } 0 ${ theme.size.size_10 };
            `;
        }
        
        return `
            line-height: ${ theme.size.size_70 };
            padding: 0;
        `; 
    } };
  
    transition: all 150ms ease-in;
    width: 100%;

    ${ ( { active } )  => ( active ? '':  `
        text-overflow: ellipsis;
        white-space: nowrap;
        overflow: hidden;
    `) }

    &:-webkit-autofill,
    &:-webkit-autofill:hover,
    &:-webkit-autofill:focus,
    &:-webkit-autofill:active {
        -webkit-transition: color 9999s ease-out, background-color 9999s ease-out;
        -webkit-transition-delay: 9999s;
    }
`;

interface InputWrapperProps {
    active?: boolean;
    disabled?: boolean;
    leftBorderSquare?: boolean;
    noBorder?: boolean;
    rightBorderSquare?: boolean;
    valid?: boolean;
    width?: string;
}

export const getInputBorderStyles = ({
    active,
    disabled,
    theme,
    valid
}: {
    active?: boolean;
    disabled?: boolean;
    theme: Theme;
    valid?: boolean;
}) => `
    transition: border-color 150ms ease-in;
    background-color: ${ theme.color.white };
    border-radius: ${ theme.radius.radius_5 };
    border: 1px solid;
    border-color: ${ !valid
        ? theme.semanticColor.error
        : disabled
            ? theme.semanticColor.borderDisabled
            : active
                ? theme.semanticColor.primary
                : theme.semanticColor.border
};
    
    @media (hover: hover) {
        &:hover {
            border-color: ${ (!active && !disabled) ? theme.color.grey.grey_30 : '' };    
        }
    }
`;

const InputWrapper = styled.div<InputWrapperProps>`
    align-items: center;
    display: flex;
    justify-content: flex-end;
    padding: 0 ${ ({ theme }) => theme.size.size_20 };
    position: relative;
    transition: all 150ms ease-in;
    width: ${ ({ width }) => (width ? width : '100%') };

    ${ ({
        active,
        disabled,
        leftBorderSquare,
        noBorder,
        rightBorderSquare,
        theme,
        valid
    }) => `
        ${ getInputBorderStyles({ active, disabled, theme, valid }) }
        
        ${ noBorder ? 'border-color: transparent;' : '' }
        
        border-top-left-radius: ${ leftBorderSquare ? '0' : theme.radius.radius_5 };
        border-bottom-left-radius: ${ leftBorderSquare ? '0' : theme.radius.radius_5 };
        border-top-right-radius: ${ rightBorderSquare ? '0' : theme.radius.radius_5 };
        border-bottom-right-radius: ${ rightBorderSquare ? '0' : theme.radius.radius_5 };border-top-left-radius: ${ leftBorderSquare ? '0' : theme.radius.radius_5 };
        border-bottom-left-radius: ${ leftBorderSquare ? '0' : theme.radius.radius_5 };
        border-top-right-radius: ${ rightBorderSquare ? '0' : theme.radius.radius_5 };
        border-bottom-right-radius: ${ rightBorderSquare ? '0' : theme.radius.radius_5 };
        
        height: ${ theme.size.size_70 };

        @media (hover: hover) {
            &:hover {
                ${ noBorder ? 'border-color: transparent' : '' };

                ${ Label } {
                    color: ${ !active && !disabled ? theme.color.grey.grey_80 : '' };
                }

            ${ IconWrapper } {
                svg {
                    path {
                        fill: ${ !active && !disabled ? theme.color.grey.grey_80 : '' };
                    }
                }

                ${ InputField } {
                    ::placeholder {
                        color: ${ !active && !disabled ? theme.color.grey.grey_80 : '' };
                    }
                }
            }
        };
    ` };
`;
